import { MAGIC_SOURCE_PREFIX } from '../super'

export interface CitableSource {
  relativeId: string
  nativeId: string
}

// `:ref[text]{ids=...}` → :ref[text]{ids=...} — a backtick span is literal
// markdown, so a citation wrapped in one would render as its raw syntax.
// The span holds one whole group, in any form the repairs below accept.
const RefInInlineCodeRegex =
  /`(:ref\[[^\]`\n]*\](?:(?:\([^)`\n]*\))?\{ids=[^}`\n]*\}|\(\{ids=[^}`\n]*\}\)|\(ids=[^)`\n]*\)))`/g

// :ref[text]({ids=...}) → :ref[text]{ids=...}
const RefParenthesesWithBracesRegex = new RegExp(
  `:ref(\\[[^\\]]*\\])\\(\\{ids=(${MAGIC_SOURCE_PREFIX}[0-9]+(?:,\\s*${MAGIC_SOURCE_PREFIX}[0-9]+)*)\\}\\)`,
  'g'
)

// :ref[text](ids=...) → :ref[text]{ids=...}
const RefParenthesesNoBracesRegex = new RegExp(
  `:ref(\\[[^\\]]*\\])\\(ids=(${MAGIC_SOURCE_PREFIX}[0-9]+(?:,\\s*${MAGIC_SOURCE_PREFIX}[0-9]+)*)\\)`,
  'g'
)

// [text]{ids=...} / :cite[text]{ids=...} → :ref[text]{ids=...}
const RefMissingDirectiveRegex = new RegExp(
  `(:[A-Za-z]+)?(\\[[^\\][]*\\])\\{ids=(${MAGIC_SOURCE_PREFIX}[0-9]+(?:,\\s*${MAGIC_SOURCE_PREFIX}[0-9]+)*)\\}`,
  'g'
)

// remove spaces after commas in {ids=...} attributes
const RefIdSpacesRegex = new RegExp(
  `(\\{ids=${MAGIC_SOURCE_PREFIX}[0-9]+(?:,\\s*${MAGIC_SOURCE_PREFIX}[0-9]+)*)\\}`,
  'g'
)

// :ref[5L1T3-1, 5L1T3-2] / :ref[nativeId] / :ref[{5L1T3-1}{5L1T3-2}] — the
// ids landed inside the brackets and the attribute block is missing entirely.
// The braced run is what the server's unknown-id drop pass made of such a
// group before this repair existed, so stored answers still carry it. The
// lookahead keeps this off well-formed groups, other attribute blocks, and
// the URL form :ref[text](url){ids=...}.
const RefIdsInBracketsRegex = /:ref\[([^\]]+)\](?![({])/g

// [5L1T3-4] / [5L1T3-1][5L1T3-2] / ([5L1T3-4]) — the row label tool results
// print before each source, copied as the citation. A run of back-to-back
// groups becomes one group. Parentheses around one run go with it, as the
// chip already sets the citation apart. Lookarounds keep this off :ref groups,
// markdown links and reference-style links. A run naming the label of a
// reference definition `[5L1T3-1]: url` is a link or the definition itself,
// so it is kept as is. A legend copied from the rows, `[5L1T3-1]: Slack
// message`, is no definition, as prose is not a destination and title.
const RelativeIdList = `${MAGIC_SOURCE_PREFIX}[0-9]+(?:,\\s*${MAGIC_SOURCE_PREFIX}[0-9]+)*`
const BracketedIds = `\\[${RelativeIdList}\\]`
const BracketedIdsRun = `${BracketedIds}(?:[ \\t]*${BracketedIds})*`
const BracketedIdsRegex = new RegExp(
  `(?<![\\]\\w:])(?:\\((${BracketedIdsRun})\\)|(${BracketedIdsRun})(?![({[]))`,
  'g'
)
const LinkDestination = String.raw`(?:<[^>\n]*>|\S+)`
const LinkTitle = String.raw`(?:"[^"\n]*"|'[^'\n]*'|\([^)\n]*\))`
const ReferenceDefinitionRegex = new RegExp(
  String.raw`^[ \t]{0,3}\[([^\]]+)\]:[ \t]*${LinkDestination}(?:[ \t]+${LinkTitle})?[ \t]*$`,
  'gm'
)
const BracketLabelRegex = /\[([^\]]+)\]/g
const RelativeIdsRegex = new RegExp(`${MAGIC_SOURCE_PREFIX}[0-9]+`, 'g')

// :ref[] with no attribute block — the drop pass emptied a group whose ids
// were all unknown. Nothing to render, so the marker and its leading space go.
const EmptyRefRegex = /[ \t]?:ref\[\](?![({])/g

const RelativeIdRegex = new RegExp(`^${MAGIC_SOURCE_PREFIX}\\d+$`)

// A native id maps back only when it identifies exactly one source.
function relativeIdsByNativeId(sources: CitableSource[]): Map<string, string> {
  const index = new Map<string, string>()
  const ambiguous = new Set<string>()
  for (const { nativeId, relativeId } of sources) {
    const existing = index.get(nativeId)
    if (existing === undefined) index.set(nativeId, relativeId)
    else if (existing !== relativeId) ambiguous.add(nativeId)
  }
  for (const nativeId of ambiguous) index.delete(nativeId)
  return index
}

// CommonMark matches reference labels case-insensitively, whitespace collapsed.
function referenceLabels(text: string, labelRegex: RegExp): string[] {
  return Array.from(text.matchAll(labelRegex), ([, label = '']) =>
    label.trim().replace(/\s+/g, ' ').toLowerCase()
  )
}

export function normalizeCitations(
  text: string,
  sources: CitableSource[] = []
): string {
  const byNativeId = relativeIdsByNativeId(sources)
  const definedLabels = new Set(referenceLabels(text, ReferenceDefinitionRegex))
  return text
    .replace(RefInInlineCodeRegex, '$1')
    .replace(RefParenthesesWithBracesRegex, ':ref$1{ids=$2}')
    .replace(RefParenthesesNoBracesRegex, ':ref$1{ids=$2}')
    .replace(
      RefMissingDirectiveRegex,
      (match, directive: string | undefined, words: string, ids: string) =>
        directive === ':ref' ? match : `:ref${words}{ids=${ids}}`
    )
    .replace(RefIdSpacesRegex, (match) => match.replace(/,\s+/g, ','))
    .replace(RefIdsInBracketsRegex, (match, bracketContent: string) => {
      const tokens = bracketContent
        .replace(/\}\s*\{/g, '},{')
        .split(',')
        .map((token) => token.trim().replace(/^\{(.+)\}$/, '$1'))
      const resolved = tokens.flatMap((token) => {
        if (RelativeIdRegex.test(token)) return [token]
        const relativeId = byNativeId.get(token)
        return relativeId === undefined ? [] : [relativeId]
      })
      // display words, not ids — leave the group for the renderer to unwrap
      if (resolved.length !== tokens.length) return match
      return `:ref[]{ids=${resolved.join(',')}}`
    })
    .replace(
      BracketedIdsRegex,
      (match, inParentheses: string | undefined, bare: string | undefined) => {
        const run = inParentheses ?? bare
        if (run === undefined) return match
        const namesDefinedLabel = referenceLabels(run, BracketLabelRegex).some(
          (label) => definedLabels.has(label)
        )
        if (namesDefinedLabel) return match
        return `:ref[]{ids=${run.match(RelativeIdsRegex)?.join(',')}}`
      }
    )
    .replace(EmptyRefRegex, '')
}
